import { log } from './log';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const started = performance.now();
  try {
    const res = await fetch(path, { signal, headers: { accept: 'application/json' } });
    const ms = Math.round(performance.now() - started);
    if (!res.ok) {
      log.warn('api', `GET ${path} -> ${res.status} in ${ms}ms`);
      throw new ApiError(`Request failed: ${res.status}`, res.status);
    }
    const body = (await res.json()) as T;
    const meta = (body as { meta?: { source?: string; cached?: boolean } }).meta;
    log.debug(
      'api',
      `GET ${path} -> 200 in ${ms}ms` +
        (meta ? ` source=${meta.source} cached=${meta.cached}` : ''),
    );
    return body;
  } catch (err) {
    const ms = Math.round(performance.now() - started);
    if (err instanceof DOMException && err.name === 'AbortError') {
      log.debug('api', `GET ${path} aborted after ${ms}ms`);
      throw err;
    }
    log.warn('api', `GET ${path} failed after ${ms}ms`, err);
    throw err;
  }
}

export interface LoadResult<T> {
  data: T;
  degraded: boolean;
}

export async function loadWithFallback<T>(
  path: string,
  fallback: T,
  signal?: AbortSignal,
): Promise<LoadResult<T>> {
  try {
    const data = await getJson<T>(path, signal);
    return { data, degraded: false };
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    log.warn('api', `falling back to bundled snapshot for ${path}`);
    return { data: fallback, degraded: true };
  }
}
